import { useEffect, useState } from "react";
import {
  Card,
  Table,
  Typography,
  message,
  Button,
  Space,
  Tag,
  Input,
  Select,
  DatePicker,
  Descriptions,
  Modal,
} from "antd";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import axios from "../../api/axios";
import {
  DownloadOutlined,
  InfoCircleOutlined,
  CloseOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;
dayjs.extend(isBetween);

type ClientRow = {
  rowKey: string;

  iccid?: string | null;
  email?: string | null;

  price?: number | null;

  planName?: string | null;
  packageGb?: number | null;

  // ✅ usage (needed for status)
  usedGb?: number | null;

  assignedDate?: string | null;
  activationDate?: string | null;
  expiryDate?: string | null;

  activationCode?: string | null;
  smdpServer?: string | null;
  saleId?: number | null;

  // optional, if backend ever sends it
  templateId?: number | null;
};

export default function MyClientsPage() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<ClientRow[]>([]);
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(
    null
  );
  const [detailsRow, setDetailsRow] = useState<ClientRow | null>(null);

  const normalizeEmail = (value?: string | null) => (value || "").trim();

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/pos/Resellers/myclients");
      const data = res.data;

      const list: any[] = Array.isArray(data) ? data : data?.items ?? [];

      setRows(
        list.map((x: any) => {
          const iccid = x.iccid ?? x.ICCID ?? null;
          const templateId =
            x.templateId ?? x.templateid ?? x.TemplateId ?? null;

          const rowKey =
            (iccid && templateId ? `${iccid}-${templateId}` : iccid) ??
            String(x.id ?? x.saleId ?? "");

          return {
            rowKey,

            iccid,
            email: normalizeEmail(
              x.email ??
                x.Email ??
                x.display_email ??
                x.displayEmail ??
                x.buyer_email ??
                x.buyerEmail ??
                null
            ),

            price:
              x.price != null
                ? Number(x.price)
                : x.cost != null
                ? Number(x.cost)
                : null,

            planName:
              x.planName ??
              x.packageName ??
              x.prepaidpackagetemplatename ??
              null,

            packageGb:
              x.packageGb != null
                ? Number(x.packageGb)
                : x.packgb != null
                ? Number(x.packgb)
                : null,

            // ✅ map used GB if backend sends it (usedgb or usedGb)
            usedGb:
              x.usedGb != null
                ? Number(x.usedGb)
                : x.usedgb != null
                ? Number(x.usedgb)
                : null,

            assignedDate:
              x.assignedDate ??
              x.tsassigned ??
              x.entrydate ??
              x.createdAt ??
              null,

            activationDate:
              x.activationDate ?? x.tsactivationutc ?? x.activatedAt ?? null,

            expiryDate:
              x.expiryDate ?? x.tsexpirationutc ?? x.expiresAt ?? null,

            activationCode: x.activationCode ?? x.activationcode ?? null,
            smdpServer: x.smdpServer ?? x.smdpserver ?? null,
            saleId:
              x.saleId != null
                ? Number(x.saleId)
                : x.saleid != null
                ? Number(x.saleid)
                : null,

            templateId: templateId != null ? Number(templateId) : null,
          };
        })
      );
    } catch (e: any) {
      message.error(e?.response?.data?.message || `Failed to load clients`);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const plans = Array.from(
    new Set(
      rows
        .map((r) => (r.planName || "").trim())
        .filter((p) => p)
    )
  ).sort();

  const filteredRows = rows.filter((r) => {
    const term = search.trim().toLowerCase();
    if (term) {
      const emailMatch = (r.email || "").toLowerCase().includes(term);
      const iccidMatch = (r.iccid || "").toLowerCase().includes(term);
      if (!emailMatch && !iccidMatch) return false;
    }

    if (planFilter && (r.planName || "").trim() !== planFilter) {
      return false;
    }

    if (dateRange) {
      const [start, end] = dateRange;
      const assigned = r.assignedDate ? dayjs(r.assignedDate) : null;
      if (
        !assigned ||
        !start ||
        !end ||
        !assigned.isBetween(start, end, "day", "[]")
      ) {
        return false;
      }
    }

    return true;
  });

  const fmt = (v: any) => (v ? dayjs(v).format("YYYY-MM-DD HH:mm") : "-");

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success("Copied");
    } catch {
      message.error("Copy failed");
    }
  };

  const formatGb = (value?: number | null) =>
    value != null ? `${Number(value).toFixed(2)} GB` : "-";

  const renderCopyValue = (value?: string | null) => {
    if (!value) return <Text type="secondary">-</Text>;
    return (
      <Space size="small" wrap style={{ maxWidth: "100%" }}>
        <Text style={{ maxWidth: 320, wordBreak: "break-all" }}>{value}</Text>
        <Button size="small" onClick={() => copy(value)}>
          Copy
        </Button>
      </Space>
    );
  };

  const buildLpaString = (row: ClientRow) => {
    const smdp = (row.smdpServer || "").trim();
    const code = (row.activationCode || "").trim();
    if (!smdp || !code) return null;
    return `LPA:1$${smdp}$${code}`;
  };

  // ✅ Status logic (no extra DB column needed)
  const getStatus = (row: ClientRow) => {
    const now = dayjs();

    // Expired has priority
    if (row.expiryDate && dayjs(row.expiryDate).isBefore(now)) {
      return { label: "Expired", color: "red" as const };
    }

    // Not activated
    if (!row.activationDate) {
      return { label: "Not activated", color: "default" as const };
    }

    // Spent (if we have usage + package size)
    if (
      row.packageGb != null &&
      row.usedGb != null &&
      row.usedGb >= row.packageGb
    ) {
      return { label: "Spent", color: "volcano" as const };
    }

    // Activated (default for activated sims)
    return { label: "Activated", color: "green" as const };
  };

  const downloadQr = async (row: ClientRow) => {
    const lpa = buildLpaString(row);
    if (!lpa) {
      message.error("Missing SM-DP+ or Activation Code");
      return;
    }

    try {
      // lazy-load so it doesn't bloat initial bundle
      const QRCode = (await import("qrcode")).default;

      const dataUrl = await QRCode.toDataURL(lpa, {
        margin: 2,
        width: 320,
      });

      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `${row.iccid || "esim"}-qr.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      message.success("eSIM QR downloaded");
    } catch (err) {
      console.error(err);
      message.error("Failed to generate QR");
    }
  };

  const renderDetailsModalContent = (row: ClientRow) => {
    const canDownload = !!buildLpaString(row);

    return (
      <div style={{ minWidth: 280 }}>
        <Descriptions size="small" column={1}>
          <Descriptions.Item label="GB">{formatGb(row.packageGb)}</Descriptions.Item>
          <Descriptions.Item label="Used">{formatGb(row.usedGb)}</Descriptions.Item>
          <Descriptions.Item label="Activated">{fmt(row.activationDate)}</Descriptions.Item>
          <Descriptions.Item label="Expires">{fmt(row.expiryDate)}</Descriptions.Item>
          <Descriptions.Item label="Activation Code">
            {renderCopyValue(row.activationCode)}
          </Descriptions.Item>
          <Descriptions.Item label="SM-DP+">
            {renderCopyValue(row.smdpServer)}
          </Descriptions.Item>
          <Descriptions.Item label="Download eSIM">
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => downloadQr(row)}
              disabled={!canDownload}
            >
              Download eSIM
            </Button>
          </Descriptions.Item>
        </Descriptions>
      </div>
    );
  };

  const columns = [
    {
      title: "ICCID",
      dataIndex: "iccid",
      key: "iccid",
      render: (v: any) => v || "-",
    },
    {
      title: "User Email",
      dataIndex: "email",
      key: "email",
      render: (v: any) => v || "-",
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      align: "right" as const,
      render: (v: any) => (v != null ? Number(v).toFixed(2) : "-"),
    },
    {
      title: "Plan",
      dataIndex: "planName",
      key: "planName",
      render: (v: any) => v || "-",
    },

    // ✅ Status column
    {
      title: "Status",
      key: "status",
      render: (_: any, row: ClientRow) => {
        const s = getStatus(row);
        return <Tag color={s.color}>{s.label}</Tag>;
      },
    },

    {
      title: "Assigned",
      dataIndex: "assignedDate",
      key: "assignedDate",
      render: (v: any) => fmt(v),
    },
    {
      title: "Details",
      key: "details",
      render: (_: any, row: ClientRow) => (
        <Button size="small" icon={<InfoCircleOutlined />} onClick={() => setDetailsRow(row)} />
      ),
    },
  ];

  return (
    <div>
      <Title level={3} style={{ margin: 0 }}>
        My Clients
      </Title>
      <Text type="secondary">
        Clients/packages synced from DB (sales + client_packages)
      </Text>

      <Card
        style={{ marginTop: 16 }}
        title="Clients"
        bodyStyle={{ overflowX: "auto" }}
        extra={
        <Space wrap>
          <Input.Search
            placeholder="Search email or ICCID"
            allowClear
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 220 }}
          />
          <Select
            allowClear
            placeholder="Plan"
            style={{ width: 160 }}
            value={planFilter || undefined}
            onChange={(val) => setPlanFilter(val || null)}
            options={plans.map((p) => ({ value: p, label: p }))}
            getPopupContainer={(trigger) => trigger.parentElement || document.body}
          />
          <DatePicker.RangePicker
            allowClear
            onChange={(range) => {
              if (!range || !range[0] || !range[1]) {
                setDateRange(null);
                return;
              }
              setDateRange([range[0], range[1]]);
            }}
            getPopupContainer={(trigger) => trigger.parentElement || document.body}
          />
        </Space>
      }
      >
        <Table
          rowKey={(r) => r.rowKey}
          loading={loading}
          columns={columns as any}
          dataSource={filteredRows}
          pagination={{
            defaultPageSize: 20,
            pageSizeOptions: ["5", "10", "20"],
            showSizeChanger: true,
          }}
          scroll={{ x: "max-content" }}
        />
      </Card>

      <Modal
        open={!!detailsRow}
        title="Details"
        centered
        width={560}
        wrapClassName="details-modal"
        style={{ maxWidth: "92vw" }}
        onCancel={() => setDetailsRow(null)}
        footer={
          <Button onClick={() => setDetailsRow(null)} icon={<CloseOutlined />}>
            Close
          </Button>
        }
      >
        {detailsRow ? renderDetailsModalContent(detailsRow) : null}
      </Modal>
    </div>
  );
}
